import { useCallback } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import { clearSearchAreaBoundary, setSearchAreaBoundary } from '@user/store/map'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { logger } from '@user/utils/logger'
import { fetchBoundaryRings, inferBoundaryQuery } from './boundaryUtils'

/**
 * MapLibre replacement for useGetMapBoundary's drawing path. It only resolves
 * the region and fetches the rings, then publishes them to Redux
 * (`searchAreaBoundary`); useMapLibreMarkers renders them as a polygon layer.
 * Framing the viewport is handled by the existing search pan, so this hook does
 * not move the camera.
 */
export function useMapLibreBoundary() {
  const dispatch = useAppDispatch()

  const drawBoundaryForHereResult = useCallback(
    async (result: HereResult) => {
      const query = inferBoundaryQuery(result)
      if (!query) {
        // Not a mappable region (e.g. a house/place result) — clear any prior.
        dispatch(clearSearchAreaBoundary())
        return
      }
      try {
        const data = await fetchBoundaryRings(query)
        if (!data) {
          dispatch(clearSearchAreaBoundary())
          return
        }
        dispatch(
          setSearchAreaBoundary({
            rings: data.rings,
            featureName: data.featureName ?? null,
            featureId: data.featureId ?? null,
          }),
        )
      } catch (err) {
        // Swallow so the search flow continues; the polygon just won't draw.
        logger.warn('drawBoundary (maplibre) failed:', err)
        dispatch(clearSearchAreaBoundary())
      }
    },
    [dispatch],
  )

  const clearBoundary = useCallback(() => {
    dispatch(clearSearchAreaBoundary())
  }, [dispatch])

  return { drawBoundaryForHereResult, clearBoundary }
}
